/**
 * Session Timeout Manager
 * CCH Care - Version 1.0
 * 
 * HIPAA Compliance: Automatically logs out after 15 minutes of inactivity.
 * Shows a warning modal 2 minutes before timeout.
 * Resets timer on any user interaction (touch, mouse, keyboard).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../features/auth/useAuth';
import { SESSION_TIMEOUT_MS, SESSION_WARNING_MS, COLORS } from '../../config/constants';

export default function SessionTimeout() {
  const { isAuthenticated, logout, checkSession } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!isAuthenticated) return;

    setShowWarning(false);

    // Set warning timer (fires 2 min before timeout)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, SESSION_TIMEOUT_MS - SESSION_WARNING_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      setShowWarning(false);
      logout();
    }, SESSION_TIMEOUT_MS);
  }, [isAuthenticated, logout]);

  // Start timers on mount and reset on activity
  useEffect(() => {
    if (!isAuthenticated) return;

    resetTimers();

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    const handleActivity = () => resetTimers();

    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, resetTimers]);

  async function handleStayLoggedIn() {
    setShowWarning(false);
    try {
      await checkSession(); // Refreshes token
      resetTimers();
    } catch (e) {
      logout();
    }
  }

  if (!showWarning) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }}>
      <div style={{
        background: COLORS.surface, borderRadius: 20, padding: 32,
        maxWidth: 420, width: '100%', textAlign: 'center',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>
          Session Expiring Soon
        </div>
        <div style={{ fontSize: 15, color: COLORS.textMuted, marginBottom: 24, lineHeight: 1.6 }}>
          Your session will expire in about 2 minutes due to inactivity.
          Would you like to stay logged in?
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={logout}
            style={{
              flex: 1, padding: 16, borderRadius: 12,
              border: `2px solid ${COLORS.border}`, background: COLORS.surface,
              color: COLORS.text, fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}
          >
            Log Out
          </button>
          <button
            onClick={handleStayLoggedIn}
            style={{
              flex: 2, padding: 16, borderRadius: 12,
              border: 'none', background: COLORS.primary,
              color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}
